/**
 * O mascote, desenhado a partir das fotos do gato.
 *
 * Não é enfeite: o estado do gato é a principal superfície de feedback do app.
 * Um humano lê "o gato está emburrado" mais rápido do que lê um gráfico de
 * adesão, e sente mais. Metade dos indicadores que um app de saúde normalmente
 * empilha em cartões vive aqui.
 *
 * O que veio das fotos e não de suposição: o babador creme que sobe do peito
 * até o queixo, os olhos verde-oliva (a característica mais marcante dele), as
 * orelhas grandes e rosadas por dentro, e o "M" tabby na testa.
 */

export type EstadoMascote =
  | "dormindo"
  | "espreitando"
  | "cutucando"
  | "derrubando_copo"
  | "comemorando"
  | "emburrado";

interface Props {
  estado: EstadoMascote;
  tamanho?: number;
  className?: string;
}

const DESCRICAO: Record<EstadoMascote, string> = {
  dormindo: "Mostarda dormindo",
  espreitando: "Mostarda espreitando, atento",
  cutucando: "Mostarda cutucando com a pata",
  derrubando_copo: "Mostarda derrubando um copo da mesa",
  comemorando: "Mostarda comemorando",
  emburrado: "Mostarda emburrado, de orelhas baixas",
};

export function Mostarda({ estado, tamanho = 120, className = "" }: Props) {
  const orelhasBaixas = estado === "emburrado";

  // Quando a pata sobe, o rabo desce. Sem isso os dois disputam o mesmo canto
  // superior direito e a pata é lida como um segundo rabo.
  const pataLevantada = estado === "cutucando" || estado === "derrubando_copo";
  const rabo = pataLevantada
    ? "M76 84 Q 94 92 90 100"
    : estado === "comemorando"
      ? "M78 74 Q 96 70 98 50"
      : estado === "emburrado"
        ? "M78 78 Q 94 86 92 96"
        : estado === "dormindo"
          ? "M78 76 Q 96 84 86 92"
          : "M78 74 Q 98 74 96 58";

  return (
    <svg
      viewBox="0 0 120 110"
      width={tamanho}
      height={(tamanho * 110) / 120}
      className={className}
      role="img"
      aria-label={DESCRICAO[estado]}
    >
      <title>{DESCRICAO[estado]}</title>

      {/* Rabo — antes do corpo para passar por trás. A ponta é mais clara,
          como nas fotos. */}
      <path d={rabo} fill="none" stroke="rgb(var(--c-mostarda-escuro))" strokeWidth="7" strokeLinecap="round" />

      {/* Corpo */}
      <ellipse cx="52" cy="76" rx="28" ry="22" fill="rgb(var(--c-mostarda))" />

      {/* Babador: peito e barriga creme. Nas fotos ele sobe do peito até o
          queixo e ocupa boa parte da silhueta de frente. */}
      <ellipse cx="50" cy="80" rx="17" ry="17" fill="rgb(var(--c-mostarda-creme))" />

      {/* Listras do lombo */}
      <g stroke="rgb(var(--c-mostarda-escuro))" strokeWidth="3" strokeLinecap="round" opacity="0.45" fill="none">
        <path d="M70 64 Q 74 70 73 76" />
        <path d="M74 72 Q 78 77 77 82" />
      </g>

      {/* Orelhas — grandes, de filhote, com o interior bem rosado */}
      <path
        d={orelhasBaixas ? "M33 35 L 14 48 L 39 45 Z" : "M29 40 L 24 18 L 45 33 Z"}
        fill="rgb(var(--c-mostarda))"
      />
      <path
        d={orelhasBaixas ? "M67 35 L 86 48 L 61 45 Z" : "M71 40 L 76 18 L 55 33 Z"}
        fill="rgb(var(--c-mostarda))"
      />
      <path
        d={orelhasBaixas ? "M33 37 L 20 46 L 37 44 Z" : "M32 39 L 29 25 L 41 34 Z"}
        fill="rgb(var(--c-mostarda-rosa))"
      />
      <path
        d={orelhasBaixas ? "M67 37 L 80 46 L 63 44 Z" : "M68 39 L 71 25 L 59 34 Z"}
        fill="rgb(var(--c-mostarda-rosa))"
      />

      {/* Cabeça */}
      <circle cx="50" cy="50" r="24" fill="rgb(var(--c-mostarda))" />

      {/* O "M" tabby da testa */}
      <g stroke="rgb(var(--c-mostarda-escuro))" strokeWidth="3" fill="none" strokeLinecap="round" opacity="0.6">
        <path d="M40 32 Q 44 38 42 42" />
        <path d="M50 29 Q 52 36 50 40" />
        <path d="M60 32 Q 57 38 58 42" />
      </g>

      {/* Máscara clara: focinho e queixo, como nas fotos */}
      <ellipse cx="50" cy="58" rx="13" ry="10" fill="rgb(var(--c-mostarda-creme))" />

      <Olhos estado={estado} />

      {/* Nariz rosa */}
      <path d="M50 55 l -3.6 -3 h 7.2 z" fill="rgb(var(--c-mostarda-rosa))" />
      <path
        d={estado === "emburrado" ? "M46 62 Q 50 59 54 62" : "M46 59 Q 50 63 54 59"}
        stroke="rgb(var(--c-mostarda-escuro))"
        strokeWidth="2.2"
        fill="none"
        strokeLinecap="round"
      />

      {/* Bigodes claros, longos — nas fotos passam bem da largura da cabeça */}
      <g stroke="rgb(var(--c-mostarda-creme))" strokeWidth="1.7" strokeLinecap="round">
        <line x1="40" y1="56" x2="20" y2="52" />
        <line x1="40" y1="58" x2="21" y2="61" />
        <line x1="60" y1="56" x2="80" y2="52" />
        <line x1="60" y1="58" x2="79" y2="61" />
      </g>

      <Adereco estado={estado} />
    </svg>
  );
}

function Olhos({ estado }: { estado: EstadoMascote }) {
  const traco = {
    stroke: "rgb(33 27 21)",
    strokeWidth: 2.6,
    fill: "none",
    strokeLinecap: "round" as const,
  };

  if (estado === "dormindo") {
    return (
      <g {...traco}>
        <path d="M38 47 Q 42 51 46 47" />
        <path d="M54 47 Q 58 51 62 47" />
      </g>
    );
  }

  if (estado === "comemorando") {
    // Olhos em ^^ — o traço universal de contentamento, legível em 24 px.
    return (
      <g {...traco}>
        <path d="M38 49 L 42 44 L 46 49" />
        <path d="M54 49 L 58 44 L 62 49" />
      </g>
    );
  }

  if (estado === "emburrado") {
    return (
      <g>
        <path d="M37 45 L 47 48" {...traco} />
        <path d="M53 48 L 63 45" {...traco} />
        <ellipse cx="42" cy="50" rx="4.6" ry="3.2" fill="rgb(var(--c-mostarda-olho))" />
        <ellipse cx="58" cy="50" rx="4.6" ry="3.2" fill="rgb(var(--c-mostarda-olho))" />
        <ellipse cx="42" cy="50" rx="1.8" ry="3" fill="rgb(20 24 14)" />
        <ellipse cx="58" cy="50" rx="1.8" ry="3" fill="rgb(20 24 14)" />
      </g>
    );
  }

  // Espreitando, cutucando e derrubando o copo compartilham o olho arregalado.
  // A íris verde ocupa quase todo o olho, como nas fotos — é o que faz parecer
  // o Mostarda e não um gato laranja genérico.
  const raioPupila = estado === "espreitando" ? 2.9 : 3.4;
  return (
    <g>
      <ellipse cx="42" cy="47" rx="6" ry="6.4" fill="rgb(var(--c-mostarda-creme))" />
      <ellipse cx="58" cy="47" rx="6" ry="6.4" fill="rgb(var(--c-mostarda-creme))" />
      <circle cx="42" cy="47" r="5" fill="rgb(var(--c-mostarda-olho))" />
      <circle cx="58" cy="47" r="5" fill="rgb(var(--c-mostarda-olho))" />
      <circle cx="42" cy="47.3" r={raioPupila} fill="rgb(20 24 14)" />
      <circle cx="58" cy="47.3" r={raioPupila} fill="rgb(20 24 14)" />
      <circle cx="43.6" cy="45.2" r="1.4" fill="white" />
      <circle cx="59.6" cy="45.2" r="1.4" fill="white" />
    </g>
  );
}

function Adereco({ estado }: { estado: EstadoMascote }) {
  if (estado === "dormindo") {
    return (
      <g fill="rgb(var(--c-suave))" fontFamily="inherit" fontWeight="700">
        <text x="76" y="34" fontSize="11">z</text>
        <text x="86" y="24" fontSize="14">z</text>
        <text x="98" y="14" fontSize="17">z</text>
      </g>
    );
  }

  if (estado === "cutucando") {
    return (
      <g>
        <path
          d="M70 72 Q 84 66 88 50"
          stroke="rgb(var(--c-mostarda))"
          strokeWidth="11"
          strokeLinecap="round"
          fill="none"
        />
        <ellipse cx="89" cy="45" rx="9" ry="7.5" fill="rgb(var(--c-mostarda-creme))" />
        <circle cx="86" cy="42" r="1.7" fill="rgb(var(--c-mostarda-rosa))" />
        <circle cx="90" cy="41" r="1.7" fill="rgb(var(--c-mostarda-rosa))" />
        <circle cx="93.5" cy="43" r="1.7" fill="rgb(var(--c-mostarda-rosa))" />
      </g>
    );
  }

  if (estado === "derrubando_copo") {
    return (
      <g>
        <path
          d="M70 72 Q 82 64 86 52"
          stroke="rgb(var(--c-mostarda))"
          strokeWidth="11"
          strokeLinecap="round"
          fill="none"
        />
        <ellipse cx="87" cy="48" rx="9" ry="7.5" fill="rgb(var(--c-mostarda-creme))" />
        {/* Coxins: sem eles a pata clara vira um borrão sem forma legível */}
        <circle cx="84" cy="45" r="1.7" fill="rgb(var(--c-mostarda-rosa))" />
        <circle cx="88" cy="44" r="1.7" fill="rgb(var(--c-mostarda-rosa))" />
        <circle cx="91.5" cy="46" r="1.7" fill="rgb(var(--c-mostarda-rosa))" />
        {/* Copo tombando, já fora da borda */}
        <g transform="rotate(38 100 76)">
          <path d="M94 66 h 12 l -2 16 h -8 z" fill="rgb(var(--c-gordura))" opacity="0.85" />
          <ellipse cx="100" cy="66" rx="6" ry="2" fill="rgb(var(--c-gordura))" />
        </g>
        <path
          d="M108 92 q 4 4 -2 6"
          stroke="rgb(var(--c-gordura))"
          strokeWidth="2"
          fill="none"
          strokeLinecap="round"
        />
      </g>
    );
  }

  if (estado === "comemorando") {
    return (
      <g fill="rgb(var(--c-mostarda))">
        <path d="M18 20 l 2.4 5 5 2.4 -5 2.4 -2.4 5 -2.4 -5 -5 -2.4 5 -2.4 z" />
        <path d="M96 26 l 1.8 3.8 3.8 1.8 -3.8 1.8 -1.8 3.8 -1.8 -3.8 -3.8 -1.8 3.8 -1.8 z" />
      </g>
    );
  }

  return null;
}
